import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Edit, Plus, AlertCircle, Camera } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

export default function AddCategorie() {
  const [categorie, setCategorie] = useState("");
  const [categories, setCategories] = useState([]);
  const [ImgCat, setImgCat] = useState(null);
  const [editCategory, setEditCategory] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editPreviewImage, setEditPreviewImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const admin = JSON.parse(localStorage.getItem("AdminEcomme"));
  if (admin) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${admin.token}`;
  }

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const categoriesResponse = await axios.get(
          `${BackendUrl}/api/categoriesRoutes`
        );
        setCategories(categoriesResponse.data.data || []);
      } catch (error) {
        toast({
          title: "Erreur",
          description: "Impossible de charger les données",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle image preview
  const handleImageChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (isEdit) {
      setEditCategory((prev) => ({ ...prev, image: file }));
      setEditPreviewImage(URL.createObjectURL(file));
    } else {
      setImgCat(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // Create Category
  const createCategory = async () => {
    if (categorie.trim().length < 3) {
      toast({
        title: "Erreur",
        description:
          "Le nom de la catégorie doit contenir au moins 3 caractères",
        variant: "destructive",
      });
      return;
    }

    if (!ImgCat) {
      toast({
        title: "Erreur",
        description: "Veuillez télécharger une image pour la catégorie",
        variant: "destructive",
      });
      return;
    }

    if (
      categories.some(
        (cat) => cat.name.toLowerCase() === categorie.toLowerCase()
      )
    ) {
      toast({
        title: "Erreur",
        description: "Cette catégorie existe déjà",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", categorie);
    formData.append("image", ImgCat);

    try {
      const response = await axios.post(
        `${BackendUrl}/api/categoriesRoutes/create`,
        formData
      );

      toast({
        title: "Succès",
        description: response.data.message,
        style: { backgroundColor: COLORS.paleGreen, borderColor: COLORS.green },
      });

      // Refresh categories
      const updatedCategories = await axios.get(
        `${BackendUrl}/api/categoriesRoutes`
      );
      setCategories(updatedCategories.data.data || []);

      // Reset form
      setCategorie("");
      setImgCat(null);
      setPreviewImage(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          "Échec de la création de la catégorie",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Delete Category
  const deleteCategory = async (id) => {
    setIsLoading(true);
    try {
      const response = await axios.delete(
        `${BackendUrl}/api/categoriesRoutes/delete`,
        { data: { id } }
      );

      toast({
        title: "Succès",
        description: response.data.message,
        style: { backgroundColor: COLORS.paleGreen, borderColor: COLORS.green },
      });

      // Refresh categories
      const updatedCategories = await axios.get(
        `${BackendUrl}/api/categoriesRoutes`
      );
      setCategories(updatedCategories.data.data || []);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Échec de la suppression de la catégorie",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setConfirmDelete(null);
    }
  };

  // Update Category
  const updateCategory = async () => {
    if (!editCategory || editCategory.name.trim().length < 2) {
      toast({
        title: "Erreur",
        description: "Nom de catégorie invalide",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", editCategory.name.trim());

    if (editCategory.image) {
      formData.append("image", editCategory.image);
    }

    try {
      const response = await axios.put(
        `${BackendUrl}/api/categoriesRoutes/update/${editCategory.id}`,
        formData
      );

      toast({
        title: "Succès",
        description: response.data.message,
        style: { backgroundColor: COLORS.paleGreen, borderColor: COLORS.green },
      });

      // Refresh categories
      const updatedCategories = await axios.get(
        `${BackendUrl}/api/categoriesRoutes`
      );
      setCategories(updatedCategories.data.data || []);

      setEditCategory(null);
      setEditPreviewImage(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Échec de la mise à jour de la catégorie",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 min-h-screen bg-gradient-to-b from-white to-orange-50">
      <h1
        className="text-2xl md:text-3xl font-bold mb-6 text-center"
        style={{ color: COLORS.darkGreen }}
      >
        Gestion des Catégories
      </h1>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Create Category Section */}
        <Card
          className="border-0 shadow-lg overflow-hidden"
          style={{ backgroundColor: COLORS.white }}
        >
          <CardHeader
            className="pb-2"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            <CardTitle className="flex items-center gap-2 text-xl">
              <Plus className="h-5 w-5" />
              Créer une Catégorie
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <Label
                  className="text-sm font-medium mb-1 block"
                  style={{ color: COLORS.darkGreen }}
                >
                  Nom de la Catégorie
                </Label>
                <Input
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  placeholder="Entrez le nom de la catégorie"
                  className="border rounded-md p-2 w-full focus:ring-2 focus:ring-offset-1 transition-all"
                  style={{
                    borderColor: COLORS.lightGray,
                    focusRingColor: COLORS.orange,
                  }}
                />
              </div>

              <div>
                <Label
                  className="text-sm font-medium mb-1 block"
                  style={{ color: COLORS.darkGreen }}
                >
                  Image de la Catégorie
                </Label>
                <div
                  className="flex flex-col items-center justify-center space-y-3 p-4 border-2 border-dashed rounded-lg relative"
                  style={{
                    borderColor: COLORS.lightGray,
                    backgroundColor: COLORS.paleGreen,
                    height: "180px",
                  }}
                >
                  {previewImage ? (
                    <div className="w-full h-full relative">
                      <img
                        src={previewImage}
                        alt="Aperçu"
                        className="w-full h-full object-contain rounded-md"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewImage(null);
                          setImgCat(null);
                        }}
                        className="absolute top-1 right-1 bg-red-500 rounded-full p-1 text-white"
                        aria-label="Supprimer l'image"
                      >
                        <Trash2 size={16} />
                      </button>

                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          document.getElementById("categoryImage").click();
                        }}
                        className="absolute bottom-2 right-2 px-3 py-1 rounded-md text-white text-sm"
                        style={{ backgroundColor: COLORS.orange }}
                      >
                        Changer l'image
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        document.getElementById("categoryImage").click()
                      }
                      className="flex flex-col items-center justify-center text-center w-full h-full cursor-pointer"
                      type="button"
                    >
                      <Camera size={40} style={{ color: COLORS.gray }} />
                      <p
                        className="mt-2 text-sm"
                        style={{ color: COLORS.gray }}
                      >
                        Cliquez pour sélectionner une image
                      </p>
                    </button>
                  )}

                  <Input
                    type="file"
                    id="categoryImage"
                    onChange={(e) => handleImageChange(e)}
                    className="hidden"
                    accept="image/*"
                  />
                </div>
              </div>

              <Button
                onClick={createCategory}
                disabled={isLoading}
                className="w-full py-2 rounded-md text-white transition-all transform hover:scale-105"
                style={{ backgroundColor: COLORS.orange }}
              >
                {isLoading ? "Chargement..." : "Ajouter la catégorie"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Categories List Section */}
        <Card
          className="border-0 shadow-lg"
          style={{ backgroundColor: COLORS.white }}
        >
          <CardHeader
            className="pb-2"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            <CardTitle className="text-xl">Liste des Catégories</CardTitle>
          </CardHeader>
          <CardContent className="p-4 overflow-y-auto max-h-[600px]">
            {isLoading ? (
              <div className="flex justify-center items-center h-40">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
                  style={{ borderColor: COLORS.orange }}
                ></div>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-10">
                <AlertCircle
                  className="mx-auto h-12 w-12 mb-4"
                  style={{ color: COLORS.gray }}
                />
                <p style={{ color: COLORS.gray }}>Aucune catégorie trouvée</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {categories?.map((cat, index) => (
                  <motion.div
                    key={cat._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 rounded-lg shadow-sm hover:shadow-md transition-all"
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? COLORS.paleGreen : COLORS.lightOrange,
                      borderLeft: `4px solid ${COLORS.orange}`,
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      {cat.image && (
                        <div
                          className="w-12 h-12 rounded-full overflow-hidden border-2 flex-shrink-0"
                          style={{ borderColor: COLORS.green }}
                        >
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <span
                        className="font-medium capitalize text-lg"
                        style={{ color: COLORS.darkGreen }}
                      >
                        {cat.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditCategory({
                            id: cat._id,
                            name: cat.name,
                            image: null,
                          });
                        }}
                        className="rounded-full p-2 hover:bg-opacity-20 transition-all"
                        style={{
                          backgroundColor: "transparent",
                          color: COLORS.darkGreen,
                        }}
                      >
                        <Edit className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(cat);
                        }}
                        className="rounded-full p-2 hover:bg-opacity-20 transition-all"
                        style={{
                          backgroundColor: "transparent",
                          color: "#EF4444",
                        }}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Category Dialog */}
      <Dialog
        open={!!editCategory}
        onOpenChange={(open) => !open && setEditCategory(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          style={{ backgroundColor: COLORS.white }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: COLORS.darkGreen }}>
              Modifier la catégorie
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label
                className="text-sm font-medium block mb-1"
                style={{ color: COLORS.darkGreen }}
              >
                Nom de la catégorie
              </Label>
              <Input
                value={editCategory?.name || ""}
                onChange={(e) =>
                  setEditCategory((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Entrez le nom de la catégorie"
                className="border rounded-md p-2 w-full"
                style={{ borderColor: COLORS.lightGray }}
              />
            </div>
            <div>
              <Label
                className="text-sm font-medium block mb-1"
                style={{ color: COLORS.darkGreen }}
              >
                Image de la catégorie
              </Label>

              <div
                className="flex flex-col items-center justify-center space-y-3 p-4 border-2 border-dashed rounded-lg relative"
                style={{
                  borderColor: COLORS.lightGray,
                  backgroundColor: COLORS.paleGreen,
                  height: "180px",
                }}
              >
                {editPreviewImage ? (
                  <div className="w-full h-full relative">
                    <img
                      src={editPreviewImage}
                      alt="Aperçu"
                      className="w-full h-full object-contain rounded-md"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditPreviewImage(null);
                        setEditCategory((prev) => ({ ...prev, image: null }));
                      }}
                      className="absolute top-1 right-1 bg-red-500 rounded-full p-1 text-white"
                      aria-label="Supprimer l'image"
                    >
                      <Trash2 size={16} />
                    </button>

                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        document.getElementById("editCategoryImage").click();
                      }}
                      className="absolute bottom-2 right-2 px-3 py-1 rounded-md text-white text-sm"
                      style={{ backgroundColor: COLORS.orange }}
                    >
                      Changer l'image
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() =>
                      document.getElementById("editCategoryImage").click()
                    }
                    className="flex flex-col items-center justify-center text-center w-full h-full cursor-pointer"
                    type="button"
                  >
                    <Camera size={40} style={{ color: COLORS.gray }} />
                    <p className="mt-2 text-sm" style={{ color: COLORS.gray }}>
                      Cliquez pour sélectionner une nouvelle image
                    </p>
                  </button>
                )}

                <Input
                  type="file"
                  id="editCategoryImage"
                  onChange={(e) => handleImageChange(e, true)}
                  className="hidden"
                  accept="image/*"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditCategory(null);
                setEditPreviewImage(null);
              }}
              className="mr-2"
              style={{ borderColor: COLORS.gray, color: COLORS.gray }}
            >
              Annuler
            </Button>
            <Button
              onClick={updateCategory}
              disabled={isLoading}
              className="px-4 py-2 text-white"
              style={{ backgroundColor: COLORS.green }}
            >
              {isLoading ? "Mise à jour..." : "Mettre à jour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          style={{ backgroundColor: COLORS.white }}
        >
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Êtes-vous sûr de vouloir supprimer la catégorie{" "}
              <strong>{confirmDelete?.name}</strong> ? Cette action ne peut pas
              être annulée.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="mr-2"
              style={{ borderColor: COLORS.gray, color: COLORS.gray }}
            >
              Annuler
            </Button>
            <Button
              onClick={() => deleteCategory(confirmDelete?._id)}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
